import { useState, useEffect, useReducer } from 'react'
import imageCompression from 'browser-image-compression'
import useImageCaptureService from '../services/useImageCaptureService'
import { WRONG_IMAGE_TYPE_ERROR, FAILED_TO_DELETE_ERROR } from '../../constants/errors'
import { useScroller } from '..'

const dealerSubheader = "Adding photos will give us a better sense of your vehicle's condition and may save you time at the dealership."
const sell2KbbSubheader = "Adding photos will give us a better sense of your vehicle's condition."
let subheader = dealerSubheader

const initialState = {
  prospectId: '',
  photoSection: {
    front: { category: 'Front', name: 'Front', svgName: 'front', imageData: null, error: null, loading: false },
    rear: { category: 'Rear', name: 'Rear', svgName: 'rear', imageData: null, error: null, loading: false },
    driverSide: { category: 'DriversSide', name: "Driver's Side", svgName: 'driversSide', imageData: null, error: null, loading: false },
    passengerSide: { category: 'PassengerSide', name: "Passenger's Side", svgName: 'passengerSide', imageData: null, error: null, loading: false },
    interiorDashboard: { category: 'InteriorDashboard', name: 'Dashboard', svgName: 'interiorDashboard', imageData: null, error: null, loading: false },
    interiorSeating: { category: 'InteriorSeating', name: 'Seating', svgName: 'interiorSeating', imageData: null, error: null, loading: false }
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PROSPECT_ID':
      return { ...state, prospectId: action.data }
    case 'UPDATE_FRONT_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, front: { ...state.photoSection.front, ...action.data } } }
    case 'UPDATE_REAR_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, rear: { ...state.photoSection.rear, ...action.data } } }
    case 'UPDATE_DRIVER_SIDE_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, driverSide: { ...state.photoSection.driverSide, ...action.data } } }
    case 'UPDATE_PASSENGER_SIDE_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, passengerSide: { ...state.photoSection.passengerSide, ...action.data } } }
    case 'UPDATE_INTERIOR_DASHBOARD_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, interiorDashboard: { ...state.photoSection.interiorDashboard, ...action.data } } }
    case 'UPDATE_INTERIOR_SEATING_PHOTO_SECTION':
      return { ...state, photoSection: { ...state.photoSection, interiorSeating: { ...state.photoSection.interiorSeating, ...action.data } } }
    default:
      return state
  }
}

const validateUploadingImageFile = imageFile => {
  const fileType = imageFile?.type
  const fileSize = imageFile?.size
  if (fileType !== 'image/jpeg' && fileType !== 'image/jpg' && fileType !== 'image/png') {
    return {
      isValid: false,
      error: WRONG_IMAGE_TYPE_ERROR
    }
  } else if ((fileSize / (1024 * 1024)).toFixed(2) > 15) {
    return {
      isValid: false,
      error: WRONG_IMAGE_TYPE_ERROR
    }
  }
  return {
    isValid: true
  }
}

const actionTypes = {
  UPLOAD_PHOTO_SELECTED: 'UPLOAD_PHOTO_SELECTED',
  UPLOAD_SUCCESSFUL: 'UPLOAD_SUCCESSFUL',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
  UPLOAD_ERROR_TRY_AGAIN: 'UPLOAD_ERROR_TRY_AGAIN',
  DELETE_PHOTO_SELECTED: 'DELETE_PHOTO_SELECTED',
  DELETE_PHOTO_CONFIRMED: 'DELETE_PHOTO_CONFIRMED',
  DELETE_PHOTO_CANCELLED: 'DELETE_PHOTO_CANCELLED'
}

const categoryCodes = {
  Front: 1,
  Rear: 2,
  DriversSide: 3,
  PassengerSide: 4,
  InteriorDashboard: 5,
  InteriorSeating: 6
}

const fireAnalyticEvent = ({ updateGlobalState, actionType, category }) => {
  switch (actionType) {
    case actionTypes.UPLOAD_PHOTO_SELECTED:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_lnk` })
    case actionTypes.UPLOAD_SUCCESSFUL:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_suc_upload` })
    case actionTypes.UPLOAD_ERROR:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_upload_error_sub` })
    case actionTypes.UPLOAD_ERROR_TRY_AGAIN:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_upload_error_sub_try_again` })
    case actionTypes.DELETE_PHOTO_SELECTED:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_del_upload` })
    case actionTypes.DELETE_PHOTO_CONFIRMED:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_del_conf_upload` })
    case actionTypes.DELETE_PHOTO_CANCELLED:
      return updateGlobalState({ fireAnalytics: `add_photo_${categoryCodes[category]}_del_keep_upload` })
    default:
      return
  }
}

const updatePhotoSectionDataForCategory = ({ category, dispatch, imageData = null, error = null, loading = false }) => {
  switch (category) {
    case 'Front':
      return dispatch({ type: 'UPDATE_FRONT_PHOTO_SECTION', data: { imageData, error, loading } })
    case 'Rear':
      return dispatch({ type: 'UPDATE_REAR_PHOTO_SECTION', data: { imageData, error, loading } })
    case 'DriversSide':
      return dispatch({ type: 'UPDATE_DRIVER_SIDE_PHOTO_SECTION', data: { imageData, error, loading } })
    case 'PassengerSide':
      return dispatch({ type: 'UPDATE_PASSENGER_SIDE_PHOTO_SECTION', data: { imageData, error, loading } })
    case 'InteriorDashboard':
      return dispatch({ type: 'UPDATE_INTERIOR_DASHBOARD_PHOTO_SECTION', data: { imageData, error, loading } })
    case 'InteriorSeating':
      return dispatch({ type: 'UPDATE_INTERIOR_SEATING_PHOTO_SECTION', data: { imageData, error, loading } })
    default:
  }
}

const toBase64 = async file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result.split(',')[1])
    reader.onerror = error => reject(error)
  })
}

const processImageToBase64 = async imageFile => {
  const compressedFileBlob = await imageCompression(imageFile, {
    maxWidthOrHeight: 1920,
    useWebWorker: true
  })
  return await toBase64(compressedFileBlob)
}

const processImageToBlob = async imageFile => {
  return await imageCompression(imageFile, {
    maxWidthOrHeight: 1920,
    useWebWorker: true
  })
}

export default ({ prospectDocuments, prospectId, setShowModal, updateGlobalState, experienceState }) => {
  const [title, setTitle] = useState()
  const [selectedImage, setSelectedImage] = useState()
  const [{ photoSection }, dispatch] = useReducer(reducer, initialState)
  const { deleteImage, uploadImage, getAllImages, getImageUploadUrl, uploadImageToS3, validateImage } = useImageCaptureService()
  const [isNextClicked, setIsNextClicked] = useState(false)
  const [setHasScrolled, scroll] = useScroller()

  useEffect(() => {
    const conditionQuestions = prospectDocuments.documents && prospectDocuments.documents.find(document => document.id.split('/')[0] === 'qc').items.filter(item => item.section === 'Vehicle Condition')
    const pageTitle = conditionQuestions && conditionQuestions[0] ? conditionQuestions[0].section : 'n/a'
    setTitle(pageTitle)
    setSubheader(experienceState)
    dispatch({ type: 'SET_PROSPECT_ID', data: prospectId })

    setHasScrolled(true)
    scroll('body', 'top')
  }, [prospectDocuments, prospectId])

  useEffect(() => {
    getImage(prospectId, dispatch, getAllImages)
  }, [prospectId])

  const getImage = async (prospectId, dispatch, getAllImages) => {
    if (prospectId) {
      const result = await getAllImages(prospectId)
      if (result) result.map(i => updatePhotoSectionDataForCategory({ dispatch, category: i.category, imageData: { imageName: i.imageUrl } }))
    }
  }

  const setSubheader = experienceState => {
    const offerCode = experienceState.offerCode
    const isSell2KbbSubheaderWithOfferCode = offerCode && (offerCode === 'd' || offerCode === 'wc')
    if (experienceState.dealerIsDirectToKBB || experienceState.dealerIsDirectToKBBv2 || isSell2KbbSubheaderWithOfferCode) subheader = sell2KbbSubheader
  }

  useEffect(() => {
    try {
      updateGlobalState({ uploadedPhoto: photoSection && Object.values(photoSection).filter(img => img != null && img.imageData !== null).length > 0 })
      setIsNextClicked(false)
    } catch (e) {
      console.log(e)
    }
  }, [photoSection])
  const anyPhotosUploadedAndTrue = photoSection && !Object.values(photoSection).some(item => item.imageData == null || item.error != null)

  const uploadSelectedImage = async ({ category, fileName, imageFile }) => {
    updatePhotoSectionDataForCategory({ dispatch, category, loading: true })
    let error
    const imageValidationResult = validateUploadingImageFile(imageFile)
    if (!imageValidationResult.isValid) {
      updatePhotoSectionDataForCategory({ dispatch, category, error: imageValidationResult.error })
      return
    }
    // Differentiate between Initial Upload event and Retry Upload Event
    const actionType = Object.values(photoSection).filter(section => section.category === category)[0]?.error ? actionTypes.UPLOAD_ERROR_TRY_AGAIN : actionTypes.UPLOAD_PHOTO_SELECTED
    fireAnalyticEvent({ updateGlobalState, actionType, category })
    const processedImageFile = await processImageToBase64(imageFile)
    const blobImage = await processImageToBlob(imageFile)
    const formData = {
      id: prospectId,
      category,
      image: processedImageFile,
      fileName: fileName
    }
    let isUploadedSuccessfully = false
    const getImageUploadUrlResponse = await getImageUploadUrl(formData.fileName)
    if (getImageUploadUrlResponse.isSuccess) {
      const uploadImageToS3Response = await uploadImageToS3({ blobImage: blobImage, uploadUrl: getImageUploadUrlResponse.data.uploadUrl, contentType: getImageUploadUrlResponse.data.contentType })
      if (uploadImageToS3Response.isSuccess) {
        const validateImageResponse = await validateImage({
          fileName: getImageUploadUrlResponse.data.fileName,
          prospectId,
          category
        })
        if (validateImageResponse.isSuccess) {
          isUploadedSuccessfully = true
        } else {
          error = validateImageResponse.error
        }
      } else {
        error = uploadImageToS3Response.error
      }
    } else {
      error = getImageUploadUrlResponse.error
    }

    if (isUploadedSuccessfully) {
      updatePhotoSectionDataForCategory({
        dispatch,
        category,
        imageData: {
          imageName: fileName
        }
      })
      fireAnalyticEvent({ updateGlobalState, actionType: actionTypes.UPLOAD_SUCCESSFUL, category })
    } else {
      fireAnalyticEvent({ updateGlobalState, actionType: actionTypes.UPLOAD_ERROR, category })
      updatePhotoSectionDataForCategory({ dispatch, category, error: error })
    }
  }
  const deleteSelectedImage = async selectedImageData => {
    fireAnalyticEvent({ updateGlobalState, actionType: actionTypes.DELETE_PHOTO_SELECTED, category: selectedImageData.category })

    setSelectedImage(selectedImageData)
    setShowModal('DeleteImage')
  }

  const confirmDeleteImage = async () => {
    if (selectedImage) {
      const { imageData, category } = selectedImage
      fireAnalyticEvent({ updateGlobalState, actionType: actionTypes.DELETE_PHOTO_CONFIRMED, category })
      const result = await deleteImage({ prospectId, category })
      if (result) {
        updatePhotoSectionDataForCategory({ dispatch, category, imageData: null, error: null })
      } else {
        updatePhotoSectionDataForCategory({ dispatch, category, imageData, error: FAILED_TO_DELETE_ERROR })
      }
    }
    setSelectedImage(null)
    setShowModal(false)
  }
  const confirmKeepImage = () => {
    const { imageData, category } = selectedImage
    fireAnalyticEvent({ updateGlobalState, actionType: actionTypes.DELETE_PHOTO_CANCELLED, category })
    updatePhotoSectionDataForCategory({ dispatch, category, imageData, error: null })
    setShowModal(false)
  }

  const showPhotoGuidelinesModal = () => {
    setShowModal('photoGuidelines')
  }

  const onNextPageSelected = (photoUploadedUrls = []) => {
    setIsNextClicked(true)
    let taggingEvent
    if (photoUploadedUrls.length > 0) {
      taggingEvent = photoUploadedUrls.map((img, index) => `pic${index + 1}_${img ? 'yes' : 'no'}`).join(',')
    } else {
      taggingEvent = Object.values(photoSection)
        .map(section => `pic${categoryCodes[section.category]}_${section.imageData ? 'yes' : 'no'}`)
        .join(',')
    }

    if (experienceState.cobrandingInfo?.isRequirePhoto && taggingEvent.includes('no')) {
      updateGlobalState({ fireAnalytics: 'image_capture_continue_error' })
    }

    setTimeout(
      (updateState, taggingEvent) => {
        updateState({ fireAnalytics: taggingEvent })
      },
      1000,
      updateGlobalState,
      taggingEvent
    )
  }

  return {
    photoSection,
    subheader,
    title,
    selectedImage,
    confirmDeleteImage,
    confirmKeepImage,
    uploadSelectedImage,
    deleteSelectedImage,
    showPhotoGuidelinesModal,
    onNextPageSelected,
    anyPhotosUploadedAndTrue,
    isNextClicked
  }
}
